import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserMention } from '@/components/UserMention';
import { UserPicker } from '@/components/UserPicker';
import { RolePicker } from '@/components/RolePicker';

/**
 * Warnings page — per-guild list of moderation warnings.
 *
 * Rows are sorted newest-first. Add-form takes User-ID + reason; delete lives
 * per row. All writes go through /api/guilds/:id/warnings and land in the same
 * table the Bot's Warn/RemoveWarn commands use.
 */

interface Warning {
  id: string;
  guildId: string;
  userId: string;
  moderatorId: string;
  reason: string;
  createdAt: string;
}

export function Warnings() {
  const { guildId } = useParams<{ guildId: string }>();
  const qc = useQueryClient();

  const listQuery = useQuery({
    queryKey: ['warnings', guildId],
    queryFn: () => api<{ warnings: Warning[] }>(`/api/guilds/${guildId}/warnings`),
    enabled: !!guildId,
  });

  const [userId, setUserId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [filterUserId, setFilterUserId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const addMut = useMutation({
    mutationFn: (body: { userId: string; reason: string }) =>
      api(`/api/guilds/${guildId}/warnings`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setUserId(null);
      setReason('');
      setFormError(null);
      qc.invalidateQueries({ queryKey: ['warnings', guildId] });
    },
    onError: (err: Error) => setFormError(err.message),
  });

  const deleteMut = useMutation({
    mutationFn: (warnId: string) =>
      api(`/api/guilds/${guildId}/warnings/${warnId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warnings', guildId] }),
  });

  const items = listQuery.data?.warnings ?? [];
  const visible = useMemo(() => {
    if (!filterUserId) return items;
    return items.filter((w) => w.userId === filterUserId);
  }, [items, filterUserId]);

  const perUserCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const w of items) map.set(w.userId, (map.get(w.userId) ?? 0) + 1);
    return [...map.entries()].sort((a, b) => b[1] - a[1]);
  }, [items]);

  const canSubmit = !!userId && reason.trim().length > 0 && !addMut.isPending;

  return (
    <div className="mx-auto max-w-3xl">
      <Link
        to={`/server/${guildId}`}
        className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Zurück zur Server-Übersicht
      </Link>
      <h1 className="mb-6 text-2xl font-semibold">Warnungen</h1>

      <WarnRolesCard guildId={guildId!} />

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">Neue Warnung ausstellen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>User</Label>
            <UserPicker guildId={guildId!} value={userId} onChange={setUserId} />
          </div>
          <div>
            <Label>Grund</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Grund der Verwarnung"
            />
          </div>
          {formError && <p className="text-sm text-destructive">{formError}</p>}
          <div className="flex justify-end">
            <Button
              disabled={!canSubmit}
              onClick={() => addMut.mutate({ userId: userId!, reason: reason.trim() })}
            >
              {addMut.isPending ? 'Speichere…' : 'Warnung ausstellen'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {perUserCounts.length > 0 && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-lg">Verwarnte User</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setFilterUserId('')}
                className={`rounded-full px-3 py-1 text-xs ${
                  filterUserId === ''
                    ? 'bg-primary text-primary-foreground'
                    : 'border bg-background hover:bg-accent'
                }`}
              >
                Alle ({items.length})
              </button>
              {perUserCounts.map(([uid, count]) => (
                <button
                  key={uid}
                  type="button"
                  onClick={() => setFilterUserId(uid)}
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs ${
                    filterUserId === uid
                      ? 'bg-primary text-primary-foreground'
                      : 'border bg-background hover:bg-accent'
                  }`}
                >
                  <UserMention id={uid} />
                  <span className="opacity-70">· {count}</span>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {filterUserId ? 'Warnungen für gewählten User' : 'Alle Warnungen'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {listQuery.isLoading && <p className="text-muted-foreground">Lade…</p>}
          {!listQuery.isLoading && visible.length === 0 && (
            <p className="text-sm text-muted-foreground">Keine Einträge.</p>
          )}
          <ul className="space-y-2">
            {visible.map((w) => (
              <li
                key={w.id}
                className="flex items-start justify-between rounded-md border p-3 text-sm"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <UserMention id={w.userId} />
                    <span className="text-xs text-muted-foreground">
                      von <UserMention id={w.moderatorId} />
                    </span>
                    <span className="text-xs text-muted-foreground">
                      · {new Date(w.createdAt).toLocaleString('de-DE')}
                    </span>
                  </div>
                  <div className="whitespace-pre-wrap">{w.reason}</div>
                  <code className="text-[10px] text-muted-foreground">{w.id}</code>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={deleteMut.isPending}
                  onClick={() => {
                    if (confirm('Warnung wirklich entfernen?')) {
                      deleteMut.mutate(w.id);
                    }
                  }}
                  title="Entfernen"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

// --------------------------- WarnRoles-Config ---------------------------

function WarnRolesCard({ guildId }: { guildId: string }) {
  const qc = useQueryClient();
  const rolesQuery = useQuery({
    queryKey: ['warn-roles', guildId],
    queryFn: () => api<{ roles: Array<string | null> }>(`/api/guilds/${guildId}/warn-roles`),
  });

  const [roles, setRoles] = useState<Array<string | null>>([null, null, null]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (rolesQuery.data) {
      setRoles(rolesQuery.data.roles);
      setDirty(false);
    }
  }, [rolesQuery.data]);

  const saveMut = useMutation({
    mutationFn: () =>
      api(`/api/guilds/${guildId}/warn-roles`, {
        method: 'PUT',
        body: JSON.stringify({ roles }),
      }),
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['warn-roles', guildId] });
    },
  });

  const set = (i: number, v: string | null) => {
    setRoles((prev) => prev.map((r, j) => (j === i ? v : r)));
    setDirty(true);
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-lg">Warn-Rollen</CardTitle>
        <p className="text-xs text-muted-foreground">
          Welche Rolle wird bei welcher Warn-Stufe vergeben. Der Bot entfernt
          beim Warnen zuerst alle vorherigen Warn-Rollen. Leer lassen = kein
          Rollen-Vergabe für diese Stufe (Bot sucht als Fallback nach dem
          Rollennamen <code>WARN-1</code>/<code>WARN-2</code>/<code>WARN-3</code>).
        </p>
      </CardHeader>
      <CardContent className="space-y-2">
        {[1, 2, 3].map((level, i) => (
          <div key={level} className="flex items-center gap-2">
            <Label className="w-20 shrink-0">Warn {level}</Label>
            <div className="min-w-0 flex-1">
              <RolePicker
                guildId={guildId}
                value={roles[i]}
                onChange={(v) => set(i, v)}
              />
            </div>
          </div>
        ))}
        <div className="flex items-center justify-end gap-2 pt-1">
          {saveMut.isSuccess && !dirty && (
            <span className="text-sm text-success">Gespeichert ✓</span>
          )}
          {saveMut.error && (
            <span className="text-sm text-destructive">
              {(saveMut.error as Error).message}
            </span>
          )}
          <Button
            size="sm"
            disabled={!dirty || saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? 'Speichere…' : 'Speichern'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
